import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Gift, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { GIFT_PRICE_LABEL, GIFT_DRAFT_KEY } from "@/data/categories";
import { PayButtons } from "@/components/PayButtons";

const FACTS = ["One time payment", "No GST applies", "Gift valid for three years", "Single use redemption"];
const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

const Summary = ({ g }) => (
  <div data-testid="gift-checkout-summary" className="rounded-2xl border-2 border-[#D9A036] bg-[#FFFBF2] p-6">
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D53]"><Gift size={16} weight="duotone" /> Your order</p>
    <h2 data-testid="gift-checkout-product" className="mt-2 font-serif text-3xl text-[#2D2824]">Gift Lifetime Access</h2>
    <p data-testid="gift-checkout-price" className="mt-1 whitespace-nowrap font-serif text-4xl font-semibold text-[#E06D53]">{GIFT_PRICE_LABEL}</p>
    <ul className="mt-4 space-y-2">
      {FACTS.map((f) => (
        <li key={f} className="flex items-center gap-2 text-[#4A4540]"><CheckCircle size={20} weight="fill" className="shrink-0 text-[#4A8B6E]" /> {f}</li>
      ))}
    </ul>
    <div className="mt-5 space-y-1 border-t border-[#D9A036]/30 pt-4 text-sm text-[#4A4540]">
      <p data-testid="gift-checkout-recipient"><strong>For:</strong> {g.recipient_name} <span className="break-all">({g.recipient_email})</span></p>
      <p><strong>From:</strong> {g.from_name}{g.occasion ? ` · ${g.occasion}` : ""}</p>
      <p data-testid="gift-checkout-delivery"><strong>Delivery:</strong> {g.deliver_on ? `${fmtDay(g.deliver_on)}, 8am Sydney time` : "Emailed straight after payment"}</p>
      {g.message && <p className="break-words italic">“{g.message}”</p>}
    </div>
  </div>
);

export default function GiftCheckout() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const draft = (() => { try { return JSON.parse(sessionStorage.getItem(GIFT_DRAFT_KEY)); } catch { return null; } })();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!draft) { navigate("/gift", { replace: true }); return; }
    if (params.get("cancelled")) {
      toast("Payment cancelled, nothing was charged.", { id: "gift-cancelled", description: "Your gift wasn't created. You can try again below." });
      navigate("/gift/checkout", { replace: true });
    }
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) return null;
  const onPaid = (r) => {
    sessionStorage.removeItem(GIFT_DRAFT_KEY);
    navigate(`/gift/done?code=${r.gift_code || ""}&order=${r.order_number || ""}`);
  };

  const body = (
    <div data-testid="gift-checkout-page" className="mx-auto grid max-w-5xl gap-8 px-5 py-8 sm:px-6 sm:py-10 lg:grid-cols-2 [&>*]:min-w-0">
      <div>
        <h1 className="font-serif text-4xl text-[#2D2824] sm:text-5xl">Payment</h1>
        <p className="mt-2 text-[#6E665E]">Secure checkout for your gift.</p>
        <div className="mt-6"><Summary g={draft.payload.gift} /></div>
        <button data-testid="gift-checkout-edit" onClick={() => navigate("/gift?edit=1")}
          className="mt-4 flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-[#36858B] hover:bg-[#EBF4F5]">
          <PencilSimple size={16} /> Edit gift details
        </button>
      </div>
      <div className="self-start rounded-2xl bg-white p-6 ring-1 ring-[#EBE4DA] lg:mt-28">
        <p className="mb-1 font-semibold text-[#2D2824]">Choose how to pay</p>
        <p className="mb-4 text-sm text-[#8A7F76]">You'll pay <span className="whitespace-nowrap font-semibold text-[#2D2824]">{GIFT_PRICE_LABEL}</span>. The gift code is only created after the payment is confirmed.</p>
        {cfg ? (
          <PayButtons payload={draft.payload} refundOk={draft.refundOk} paypalReady={!!cfg.client_id} paypalMode={cfg.mode}
            cardEnabled={cfg.card_enabled !== false} onPaid={onPaid} cardLabel="Pay by Card" testPrefix="gift" />
        ) : <p className="text-sm text-[#8A7F76]">Loading payment options…</p>}
        {cfg && cfg.stripe_mode !== "live" && cfg.card_enabled !== false && (
          <p data-testid="gift-checkout-test-note" className="mt-3 text-center text-xs text-[#8A7F76]">Card payments are in test mode · use card 4242 4242 4242 4242</p>
        )}
      </div>
    </div>
  );

  return cfg?.client_id ? (
    <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: "AUD", intent: "capture", components: "buttons", disableFunding: "card" }}>
      {body}
    </PayPalScriptProvider>
  ) : body;
}
